import React, { useEffect, useState } from 'react';

import { Form, Formik } from 'formik';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import Input from '@components/common/Formik/FormikInputField';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Text from '@components/common/Text/Text';
import Choice from '@components/lpc/Pot/AmountModule/Settings/SettingsModal/Revives/Choice/Choice';
import ChoiceNotFound from '@components/lpc/Pot/AmountModule/Settings/SettingsModal/Revives/ChoiceNotFound/ChoiceNotFound';
import { inviteType } from '@propTypes/inviteType';
import potService from '@services/domain/Lpc/PotService';
import ObjectService from '@services/utils/ObjectService';

import css from './Revives.module.scss';

type RevivesProps = {
    onCloseModal: () => void;
    potRef: string;
    revives: inviteType[];
    onUpdateRevives: () => void;
    onShowReviveSuccessModal: () => void;
};

const Revives = ({ potRef, onCloseModal, revives, onUpdateRevives, onShowReviveSuccessModal }: RevivesProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const [emails, setEmails] = useState<inviteType[]>(revives);
    const [displayedEmails, setDisplayedEmails] = useState<inviteType[]>(revives);
    const [selectedEmails, setSelectedEmails] = useState<inviteType[]>([]);
    const [searchedValue, setSearchedValue] = useState<string>(null);
    const [currentStep, setCurrentStep] = useState<number>(1);

    const [isLoading, setIsLoading] = useState(false);
    const [selectAll, setSelectAll] = useState<boolean>(false);

    const [displayedWording, setDisplayedWording] = useState({
        title: t('settings.revives.1.title'),
        desc: t('settings.revives.1.desc'),
    });
    const { addToast } = useToasts();

    useEffect(() => {
        setDisplayedWording({
            title: t(`settings.revives.${currentStep}.title`),
            desc:
                currentStep === 2 && selectedEmails.length > 1
                    ? t(`settings.revives.${currentStep}.desc-plural`, { count: selectedEmails.length })
                    : t(`settings.revives.${currentStep}.desc`),
        });
    }, [currentStep]);

    const toggleSelectedEmail = (index: number) => {
        const emailsTemp = emails.map((e) => {
            if (e.index === index) {
                e.selected = !e.selected;
            }
            return e;
        });
        setEmails(emailsTemp);
        const selectedEmailTemp = emails.filter((e) => {
            if (e.selected) {
                return e;
            }
        });
        setSelectedEmails(selectedEmailTemp);
        onSearch(searchedValue);
        setSelectAll(selectedEmailTemp.length === emails.length);
    };

    const onSubmit = (values) => {
        setIsLoading(true);

        potService
            .sendRevives(potRef, { inviteIDs: selectedEmails.map((e) => e.inviteID), message: values.message })
            .then(() => {
                onUpdateRevives();
                onShowReviveSuccessModal();
            })
            .then(() => onCloseModal())
            .catch((err) => {
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                });
            })
            .finally(() => setIsLoading(false));
    };

    const onSelectAll = () => {
        const selectedEmailsTemp = emails.map((e) => {
            e.selected = !selectAll;
            return e;
        });
        !selectAll ? setSelectedEmails(selectedEmailsTemp) : setSelectedEmails([]);
        setEmails(selectedEmailsTemp);
        setSelectAll(!selectAll);
        setDisplayedEmails(selectedEmailsTemp);
    };

    const onSearch = (value: string) => {
        setSearchedValue(value);
        if (!value || !value.trim()) {
            setDisplayedEmails(emails);
        } else {
            setDisplayedEmails(
                emails.filter((e) => {
                    if (e.email.includes(value)) {
                        return e;
                    }
                }),
            );
        }
    };

    return (
        <>
            <Formik initialValues={{ search: '' }} onSubmit={(values) => onSubmit(values)}>
                {({ values }) => (
                    <Form>
                        <>
                            <Text variant={'caption_00'} color={'txt-primary'}>
                                {displayedWording.title}
                            </Text>
                            <Text variant={'body_02'} color={'txt-secondary'}>
                                {displayedWording.desc}
                            </Text>

                            {currentStep === 1 && (
                                <>
                                    <div className={css.form__revives__input__collapse}>
                                        <Input
                                            onHandleChange={(event) => {
                                                onSearch(event.target.value);
                                            }}
                                            name="search"
                                            type="text"
                                            placeholder={t('settings.revives.search-placeholder')}
                                        />
                                    </div>
                                    <div className={css.form__revives__choices}>
                                        {ObjectService.hasContent(displayedEmails) ? (
                                            <>
                                                {displayedEmails.length === emails.length && (
                                                    <>
                                                        <Choice
                                                            customClass={css.form__revives__choices__select_all}
                                                            revivedCount={0}
                                                            email={t('settings.revives.select-all')}
                                                            selected={selectAll}
                                                            onClick={() => {
                                                                onSelectAll();
                                                            }}
                                                        />
                                                        <div className={css.form__revives__divider} />
                                                    </>
                                                )}
                                                {displayedEmails.map((element, i) => (
                                                    <Choice
                                                        revivedCount={element.revivedCount}
                                                        onClick={() => {
                                                            toggleSelectedEmail(element.index);
                                                        }}
                                                        email={displayedEmails[i].email}
                                                        selected={displayedEmails[i].selected}
                                                        key={i}
                                                    />
                                                ))}
                                            </>
                                        ) : (
                                            <ChoiceNotFound value={searchedValue} />
                                        )}
                                    </div>

                                    <Text variant={'body_00'} color={'txt-secondary'}>
                                        {ObjectService.hasContent(selectedEmails)
                                            ? t('settings.revives.selected-contact', {
                                                  count: selectedEmails.length,
                                              })
                                            : t(`settings.revives.no-selected-contacts`)}
                                    </Text>
                                </>
                            )}

                            {currentStep === 2 && (
                                <Input
                                    height={'300px'}
                                    customStyle={css.form__revives__input__text}
                                    name="message"
                                    type="textarea"
                                    placeholder={t('settings.revives.revive-placeholder')}
                                />
                            )}

                            {/* SUBMIT */}
                            <div className={css.form__revives__submit}>
                                {currentStep === 2 && (
                                    <Button
                                        type={'button'}
                                        customClass={css.form__revives__submit__cta}
                                        variant={'secondary'}
                                        onClick={() => setCurrentStep(1)}
                                    >
                                        {t('settings.revives.return')}
                                    </Button>
                                )}
                                <Button
                                    type={'button'}
                                    margin={'0 0 0 8px'}
                                    variant={'primary'}
                                    customClass={css.form__revives__submit__cta}
                                    isLoading={isLoading}
                                    onClick={() => (currentStep === 1 ? setCurrentStep(2) : onSubmit(values))}
                                    isDisabled={ObjectService.isEmpty(selectedEmails) || isLoading}
                                >
                                    {currentStep === 1 ? t('settings.revives.next') : t('settings.revives.revive')}
                                </Button>
                            </div>
                        </>
                    </Form>
                )}
            </Formik>
        </>
    );
};
export default Revives;
